import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  FaTimes,
  FaSyncAlt,
  FaTable,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
} from "react-icons/fa";
import { request } from "../../services/api";

const getCurrentThemeKey = () => {
  if (typeof document === "undefined") return "default";
  return document.documentElement.getAttribute("data-sfarm-theme") || "default";
};

const ARCHIVE_THEME_PALETTES = {
  default: {
    isLight: false,
    overlay: "rgba(0, 0, 0, 0.65)",
    modalBg:
      "linear-gradient(145deg, rgba(22, 36, 71, 0.98), rgba(15, 23, 42, 0.98))",
    headerBg: "rgba(15, 23, 42, 0.92)",
    toolbarBg: "rgba(0, 0, 0, 0.08)",
    color: "#fff5e6",
    muted: "rgba(255, 245, 230, 0.68)",
    border: "rgba(255, 245, 230, 0.12)",
    inputBg: "rgba(0, 0, 0, 0.24)",
    inputColor: "#ffffff",
    primary: "#73c2fb",
    primaryColor: "#162447",
    tableHeaderBg: "rgba(15, 23, 42, 0.96)",
    tableHeaderColor: "#f1c40f",
    tableText: "#ecf0f1",
    rowBorder: "rgba(255, 255, 255, 0.08)",
    empty: "rgba(255, 245, 230, 0.68)",
    icon: "#73c2fb",
    shadow: "0 28px 90px rgba(0, 0, 0, 0.58)",
  },
  "smart-home": {
    isLight: false,
    overlay: "rgba(0, 0, 0, 0.68)",
    modalBg:
      "linear-gradient(145deg, rgba(12, 23, 48, 0.98), rgba(5, 12, 28, 0.98))",
    headerBg: "rgba(6, 17, 40, 0.92)",
    toolbarBg: "rgba(126, 232, 255, 0.05)",
    color: "#e9fbff",
    muted: "rgba(233, 251, 255, 0.68)",
    border: "rgba(126, 232, 255, 0.18)",
    inputBg: "rgba(0, 0, 0, 0.28)",
    inputColor: "#ffffff",
    primary: "#4de1ff",
    primaryColor: "#061128",
    tableHeaderBg: "rgba(6, 17, 40, 0.96)",
    tableHeaderColor: "#4de1ff",
    tableText: "#e9fbff",
    rowBorder: "rgba(126, 232, 255, 0.12)",
    empty: "rgba(233, 251, 255, 0.66)",
    icon: "#4de1ff",
    shadow: "0 28px 90px rgba(0, 0, 0, 0.6)",
  },
  "industrial-blue": {
    isLight: true,
    overlay: "rgba(18, 38, 58, 0.38)",
    modalBg:
      "linear-gradient(145deg, rgba(229, 243, 251, 0.97), rgba(207, 230, 244, 0.96))",
    headerBg: "rgba(245, 250, 253, 0.58)",
    toolbarBg: "rgba(255, 255, 255, 0.28)",
    color: "#18344f",
    muted: "rgba(24, 52, 79, 0.68)",
    border: "rgba(24, 52, 79, 0.16)",
    inputBg: "rgba(255, 255, 255, 0.86)",
    inputColor: "#18344f",
    primary: "#2f83c7",
    primaryColor: "#ffffff",
    tableHeaderBg: "rgba(213, 234, 247, 0.96)",
    tableHeaderColor: "#18344f",
    tableText: "#18344f",
    rowBorder: "rgba(24, 52, 79, 0.12)",
    empty: "rgba(24, 52, 79, 0.66)",
    icon: "#2f83c7",
    shadow: "0 28px 90px rgba(24, 52, 79, 0.24)",
  },
  "home-soft": {
    isLight: true,
    overlay: "rgba(31, 45, 58, 0.34)",
    modalBg:
      "linear-gradient(145deg, rgba(255, 255, 255, 0.96), rgba(240, 247, 250, 0.94))",
    headerBg: "rgba(255, 255, 255, 0.58)",
    toolbarBg: "rgba(255, 255, 255, 0.34)",
    color: "#26384c",
    muted: "rgba(38, 56, 76, 0.68)",
    border: "rgba(52, 73, 94, 0.16)",
    inputBg: "rgba(255, 255, 255, 0.9)",
    inputColor: "#26384c",
    primary: "#6a9c89",
    primaryColor: "#ffffff",
    tableHeaderBg: "rgba(240, 247, 250, 0.96)",
    tableHeaderColor: "#26384c",
    tableText: "#26384c",
    rowBorder: "rgba(52, 73, 94, 0.12)",
    empty: "rgba(38, 56, 76, 0.66)",
    icon: "#6a9c89",
    shadow: "0 28px 90px rgba(31, 45, 58, 0.22)",
  },
};

const useArchiveTheme = (isOpen) => {
  const [themeKey, setThemeKey] = useState(getCurrentThemeKey);

  useEffect(() => {
    if (!isOpen) return undefined;

    const syncTheme = () => setThemeKey(getCurrentThemeKey());
    syncTheme();

    window.addEventListener("sfarm-theme-change", syncTheme);
    window.addEventListener("storage", syncTheme);

    return () => {
      window.removeEventListener("sfarm-theme-change", syncTheme);
      window.removeEventListener("storage", syncTheme);
    };
  }, [isOpen]);

  return ARCHIVE_THEME_PALETTES[themeKey] || ARCHIVE_THEME_PALETTES.default;
};

const useViewportWidth = (isOpen) => {
  const getWidth = () => {
    if (typeof window === "undefined") return 1024;
    return window.innerWidth || 1024;
  };

  const [viewportWidth, setViewportWidth] = useState(getWidth);

  useEffect(() => {
    if (!isOpen || typeof window === "undefined") return undefined;

    const syncViewportWidth = () => setViewportWidth(getWidth());
    syncViewportWidth();

    window.addEventListener("resize", syncViewportWidth);
    window.addEventListener("orientationchange", syncViewportWidth);

    return () => {
      window.removeEventListener("resize", syncViewportWidth);
      window.removeEventListener("orientationchange", syncViewportWidth);
    };
  }, [isOpen]);

  return viewportWidth;
};

const buildArchiveStyles = (theme) => ({
  overlay: {
    position: "fixed",
    inset: 0,
    background: theme.overlay,
    zIndex: 99999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    backdropFilter: "blur(6px)",
  },
  modal: {
    width: "min(1040px, 95vw)",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    background: theme.modalBg,
    color: theme.color,
    borderRadius: "18px",
    boxShadow: theme.shadow,
    overflow: "hidden",
    border: `1px solid ${theme.border}`,
  },
  header: {
    flexShrink: 0,
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "14px 48px",
    borderBottom: `1px solid ${theme.border}`,
    background: theme.headerBg,
    direction: "rtl",
    textAlign: "center",
  },
  iconButton: {
    position: "absolute",
    left: "16px",
    top: "50%",
    transform: "translateY(-50%)",
    border: `1px solid ${theme.border}`,
    background: theme.isLight
      ? "rgba(255,255,255,0.72)"
      : "rgba(255,255,255,0.06)",
    color: theme.color,
    cursor: "pointer",
    fontSize: "16px",
    width: "34px",
    height: "34px",
    borderRadius: "11px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  toolbar: {
    flexShrink: 0,
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "18px",
    padding: "12px 16px",
    borderBottom: `1px solid ${theme.border}`,
    background: theme.toolbarBg,
    direction: "rtl",
  },
  toolbarItem: {
    flex: "1 1 0",
    display: "flex",
  },
  filterItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    direction: "rtl",
    color: theme.color,
  },
  select: {
    background: theme.inputBg,
    color: theme.inputColor,
    border: `1px solid ${theme.border}`,
    borderRadius: "8px",
    padding: "6px 8px",
    outline: "none",
  },
  refreshButton: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    background: theme.primary,
    color: theme.primaryColor,
    border: "none",
    borderRadius: "8px",
    padding: "7px 12px",
    cursor: "pointer",
    fontFamily: "inherit",
    fontWeight: 800,
  },
  info: {
    flexShrink: 0,
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "18px",
    padding: "10px 16px",
    fontSize: "12px",
    color: theme.muted,
    direction: "rtl",
    textAlign: "right",
    borderBottom: `1px solid ${theme.border}`,
  },
  infoItem: {
    flex: "1 1 0",
  },
  error: {
    flexShrink: 0,
    margin: "10px 16px 0",
    padding: "10px",
    background: "rgba(231, 76, 60, 0.15)",
    border: "1px solid rgba(231, 76, 60, 0.4)",
    color: theme.isLight ? "#9f2d22" : "#ffb3b3",
    borderRadius: "8px",
    fontSize: "13px",
  },
  tableWrapper: {
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "auto",
    padding: "0 16px 10px",
    scrollbarGutter: "stable",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0",
    minWidth: "760px",
    fontSize: "13px",
    direction: "rtl",
  },
  th: {
    position: "sticky",
    top: 0,
    background: theme.tableHeaderBg,
    color: theme.tableHeaderColor,
    padding: "11px 10px",
    borderBottom: `1px solid ${theme.border}`,
    fontWeight: 900,
    backdropFilter: "blur(10px)",
    zIndex: 2,
    textAlign: "right",
  },
  sortHeaderButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "7px",
    border: "none",
    background: "transparent",
    color: theme.tableHeaderColor,
    cursor: "pointer",
    padding: 0,
    fontFamily: "inherit",
    fontSize: "13px",
    fontWeight: 900,
    direction: "rtl",
  },
  td: {
    padding: "9px 10px",
    borderBottom: `1px solid ${theme.rowBorder}`,
    verticalAlign: "middle",
    color: theme.tableText,
  },
  rowEven: {
    background: theme.isLight
      ? "rgba(255, 255, 255, 0.64)"
      : "rgba(255, 255, 255, 0.035)",
  },
  rowOdd: {
    background: theme.isLight
      ? "rgba(225, 240, 249, 0.46)"
      : "rgba(255, 255, 255, 0.075)",
  },
  rowAbove: {
    boxShadow: theme.isLight
      ? "inset 4px 0 0 rgba(231, 76, 60, 0.78)"
      : "inset 4px 0 0 rgba(255, 99, 99, 0.82)",
  },
  rowBelow: {
    boxShadow: theme.isLight
      ? "inset 4px 0 0 rgba(46, 204, 113, 0.72)"
      : "inset 4px 0 0 rgba(57, 255, 20, 0.55)",
  },
  rowEqual: {
    boxShadow: theme.isLight
      ? "inset 4px 0 0 rgba(127, 140, 141, 0.42)"
      : "inset 4px 0 0 rgba(189, 195, 199, 0.32)",
  },
  valueBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "78px",
    padding: "5px 10px",
    borderRadius: "999px",
    fontWeight: 900,
    direction: "ltr",
    unicodeBidi: "plaintext",
  },
  valueAbove: {
    color: theme.isLight ? "#9f2d22" : "#ffb3b3",
    background: theme.isLight
      ? "rgba(231, 76, 60, 0.13)"
      : "rgba(231, 76, 60, 0.18)",
    border: "1px solid rgba(231, 76, 60, 0.30)",
  },
  valueBelow: {
    color: theme.isLight ? "#1f7a3a" : "#9cffb5",
    background: theme.isLight
      ? "rgba(46, 204, 113, 0.13)"
      : "rgba(46, 204, 113, 0.16)",
    border: "1px solid rgba(46, 204, 113, 0.30)",
  },
  valueEqual: {
    color: theme.tableText,
    background: theme.isLight
      ? "rgba(127, 140, 141, 0.10)"
      : "rgba(255, 255, 255, 0.08)",
    border: `1px solid ${theme.rowBorder}`,
  },
  setpointBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "72px",
    padding: "4px 9px",
    borderRadius: "999px",
    fontWeight: 800,
    direction: "ltr",
    unicodeBidi: "plaintext",
    color: theme.tableText,
    background: theme.isLight
      ? "rgba(255, 255, 255, 0.52)"
      : "rgba(255, 255, 255, 0.07)",
    border: `1px solid ${theme.rowBorder}`,
  },
  trendChip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    minWidth: "88px",
    padding: "5px 9px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: 900,
  },
  trendUp: {
    color: theme.isLight ? "#c0392b" : "#ff8a8a",
    background: theme.isLight
      ? "rgba(231, 76, 60, 0.10)"
      : "rgba(231, 76, 60, 0.15)",
    border: "1px solid rgba(231, 76, 60, 0.24)",
  },
  trendDown: {
    color: theme.isLight ? "#1f7a3a" : "#9cffb5",
    background: theme.isLight
      ? "rgba(46, 204, 113, 0.10)"
      : "rgba(46, 204, 113, 0.14)",
    border: "1px solid rgba(46, 204, 113, 0.24)",
  },
  trendSame: {
    color: theme.muted,
    background: theme.isLight
      ? "rgba(127, 140, 141, 0.08)"
      : "rgba(255, 255, 255, 0.06)",
    border: `1px solid ${theme.rowBorder}`,
  },
  mutedText: {
    color: theme.muted,
  },
  empty: {
    padding: "30px",
    textAlign: "center",
    color: theme.empty,
  },
  stackedList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "0 0 4px",
    direction: "rtl",
  },
  stackedCard: {
    border: `1px solid ${theme.rowBorder}`,
    borderRadius: "14px",
    padding: "10px",
    color: theme.tableText,
    background: theme.isLight
      ? "rgba(255, 255, 255, 0.58)"
      : "rgba(255, 255, 255, 0.055)",
    boxShadow: theme.isLight
      ? "0 8px 20px rgba(24, 52, 79, 0.08)"
      : "0 8px 20px rgba(0, 0, 0, 0.18)",
  },
  stackedCardAbove: {
    borderColor: "rgba(231, 76, 60, 0.34)",
    boxShadow: theme.isLight
      ? "inset 4px 0 0 rgba(231, 76, 60, 0.72)"
      : "inset 4px 0 0 rgba(255, 99, 99, 0.72)",
  },
  stackedCardBelow: {
    borderColor: "rgba(46, 204, 113, 0.30)",
    boxShadow: theme.isLight
      ? "inset 4px 0 0 rgba(46, 204, 113, 0.68)"
      : "inset 4px 0 0 rgba(57, 255, 20, 0.50)",
  },
  stackedCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    paddingBottom: "8px",
    marginBottom: "8px",
    borderBottom: `1px solid ${theme.rowBorder}`,
    fontWeight: 900,
  },
  stackedIndex: {
    color: theme.tableHeaderColor,
    fontSize: "12px",
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  stackedTimeButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    border: "none",
    background: "transparent",
    color: theme.tableHeaderColor,
    cursor: "pointer",
    padding: 0,
    fontFamily: "inherit",
    fontSize: "12px",
    fontWeight: 900,
    textAlign: "left",
    direction: "rtl",
  },
  stackedRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    minHeight: "34px",
    padding: "6px 0",
    borderBottom: `1px dashed ${theme.rowBorder}`,
  },
  stackedLabel: {
    color: theme.muted,
    fontSize: "12px",
    fontWeight: 800,
  },
  stackedValue: {
    display: "inline-flex",
    justifyContent: "flex-end",
    minWidth: 0,
    textAlign: "left",
  },
  pagination: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "8px",
    padding: "10px 16px 12px",
    borderTop: `1px solid ${theme.border}`,
    background: theme.toolbarBg,
    direction: "rtl",
  },
  paginationButton: {
    minWidth: "70px",
    border: `1px solid ${theme.border}`,
    background: theme.isLight
      ? "rgba(255, 255, 255, 0.74)"
      : "rgba(255, 255, 255, 0.07)",
    color: theme.color,
    cursor: "pointer",
    borderRadius: "9px",
    padding: "7px 10px",
    fontFamily: "inherit",
    fontWeight: 800,
  },
  paginationButtonDisabled: {
    opacity: 0.42,
    cursor: "not-allowed",
  },
  pageInfo: {
    minWidth: "150px",
    textAlign: "center",
    color: theme.muted,
    fontSize: "12px",
    fontWeight: 800,
  },
});

const ARCHIVE_FETCH_LIMIT_BY_RANGE = {
  "-1h": 5000,
  "-6h": 5000,
  "-12h": 10000,
  "-24h": 10000,
  "-7d": 20000,
  "-30d": 50000,
  "-90d": 50000,
};

const getArchiveFetchLimit = (range) =>
  ARCHIVE_FETCH_LIMIT_BY_RANGE[range] || 10000;

const getRecordTimestamp = (row) => {
  const timestamp = new Date(row?.time).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const ArchiveTableModal = ({
  isOpen,
  onClose,
  serial,
  sensorKey,
  title,
  token,
}) => {
  const [range, setRange] = useState("-24h");
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState("desc");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const archiveTheme = useArchiveTheme(isOpen);
  const archiveStyles = useMemo(
    () => buildArchiveStyles(archiveTheme),
    [archiveTheme],
  );
  const viewportWidth = useViewportWidth(isOpen);
  const isMobile = viewportWidth <= 768;

  const displaySensorName = title || sensorKey;
  const normalizedSensorKey = String(sensorKey || "").toLowerCase();
  const isDigitalArchive =
    normalizedSensorKey.startsWith("din") ||
    normalizedSensorKey.startsWith("dout");

  const loadArchive = async () => {
    const fetchLimit = getArchiveFetchLimit(range);

    console.log("Archive loadArchive called:", {
      isOpen,
      serial,
      sensorKey,
      range,
      fetchLimit,
      pageSize,
      currentPage,
      sortOrder,
      token,
    });

    if (!serial || !sensorKey) {
      setError(
        `اطلاعات سنسور کامل نیست. serial=${serial || "خالی"} sensor=${sensorKey || "خالی"}`,
      );
      setRecords([]);
      setCurrentPage(1);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const json = await request(
        `/get_sensor_archive.php?serial=${encodeURIComponent(serial)}&sensor=${encodeURIComponent(sensorKey)}&range=${encodeURIComponent(range)}&limit=${encodeURIComponent(fetchLimit)}`,
      );

      console.log("Archive API response:", json);

      if (!json || json.status !== "success") {
        throw new Error(json?.message || "خطا در دریافت آرشیو");
      }

      setRecords(json.records || []);
      setCurrentPage(1);
    } catch (err) {
      console.error("Archive fetch error:", err);
      setError(err.message || "خطای نامشخص");
      setRecords([]);
      setCurrentPage(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadArchive();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, serial, sensorKey, range]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize, sortOrder, serial, sensorKey, range]);

  if (!isOpen) return null;

  const sortedRecords = [...records].sort((a, b) => {
    const aTime = getRecordTimestamp(a);
    const bTime = getRecordTimestamp(b);

    return sortOrder === "asc" ? aTime - bTime : bTime - aTime;
  });

  const totalRecords = sortedRecords.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const safeCurrentPage = Math.min(Math.max(currentPage, 1), totalPages);
  const pageStartIndex = (safeCurrentPage - 1) * pageSize;
  const pageRecords = sortedRecords.slice(
    pageStartIndex,
    pageStartIndex + pageSize,
  );
  const pageEndIndex =
    totalRecords === 0 ? 0 : pageStartIndex + pageRecords.length;

  const goToPage = (nextPage) => {
    const normalizedPage = Math.min(Math.max(nextPage, 1), totalPages);
    setCurrentPage(normalizedPage);
  };

  const getPreviousRecordForTrend = (globalIndex) => {
    if (sortOrder === "asc") {
      return sortedRecords[globalIndex - 1];
    }

    return sortedRecords[globalIndex + 1];
  };

  const toggleSortOrder = () => {
    setSortOrder((current) => (current === "asc" ? "desc" : "asc"));
  };

  const renderTimeHeader = () => (
    <button
      type="button"
      onClick={toggleSortOrder}
      style={archiveStyles.sortHeaderButton}
      title={sortOrder === "desc" ? "جدیدترین اول" : "قدیمی‌ترین اول"}
    >
      <span>زمان</span>
      {sortOrder === "desc" ? <FaArrowDown /> : <FaArrowUp />}
    </button>
  );

  const formatDateTime = (iso) => {
    try {
      const date = new Date(iso);

      const parts = new Intl.DateTimeFormat("fa-IR-u-ca-persian-nu-arabext", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).formatToParts(date);

      const getPart = (type) =>
        parts.find((part) => part.type === type)?.value || "";

      const day = getPart("day");
      const month = getPart("month");
      const year = getPart("year");
      const hour = getPart("hour");
      const minute = getPart("minute");
      const second = getPart("second");

      return `${day} ${month} ${year}, ${hour}:${minute}:${second}`;
    } catch {
      return iso;
    }
  };

  const toNumberOrNull = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const getRowSetpoint = (row) =>
    row?.setpoint ??
    row?.setpoint_snapshot ??
    row?.threshold ??
    row?.limit_value ??
    row?.limit ??
    null;

  const getRowLimitState = (row) => {
    if (["above", "below", "equal", "unknown"].includes(row?.limit_state)) {
      return row.limit_state;
    }

    const value = toNumberOrNull(row?.value);
    const setpoint = toNumberOrNull(getRowSetpoint(row));

    if (value === null || setpoint === null) return "unknown";
    if (value > setpoint) return "above";
    if (value < setpoint) return "below";
    return "equal";
  };

  const getRowTrendState = (row, previousRow) => {
    if (["up", "down", "same", "unknown"].includes(row?.trend)) {
      return row.trend;
    }

    const value = toNumberOrNull(row?.value);
    const previousValue = toNumberOrNull(previousRow?.value);

    if (value === null || previousValue === null) return "unknown";
    if (value > previousValue) return "up";
    if (value < previousValue) return "down";
    return "same";
  };

  const formatValueWithUnit = (value, unit) => {
    if (value === null || value === undefined || value === "") return "-";
    return `${value}${unit ? ` ${unit}` : ""}`;
  };

  const formatDigitalValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";

    const number = Number(value);
    if (Number.isFinite(number))
      return number === 1 ? "۱" : number === 0 ? "۰" : String(value);

    return String(value);
  };

  const getDigitalState = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return "unknown";
    if (number === 1) return "on";
    if (number === 0) return "off";
    return "unknown";
  };

  const getDigitalBadgeStyle = (value) => {
    const state = getDigitalState(value);

    if (state === "on") {
      return {
        ...archiveStyles.valueBadge,
        ...archiveStyles.valueBelow,
        minWidth: "54px",
      };
    }

    if (state === "off") {
      return {
        ...archiveStyles.valueBadge,
        ...archiveStyles.valueEqual,
        minWidth: "54px",
      };
    }

    return {
      ...archiveStyles.valueBadge,
      ...archiveStyles.valueEqual,
      minWidth: "54px",
      opacity: 0.72,
    };
  };

  const renderDigitalCell = (value) => (
    <span style={getDigitalBadgeStyle(value)}>{formatDigitalValue(value)}</span>
  );

  const getValueBadgeStyle = (limitState) => ({
    ...archiveStyles.valueBadge,
    ...(limitState === "above" ? archiveStyles.valueAbove : {}),
    ...(limitState === "below" ? archiveStyles.valueBelow : {}),
    ...(limitState === "equal" || limitState === "unknown"
      ? archiveStyles.valueEqual
      : {}),
  });

  const renderTrend = (trendState) => {
    if (trendState === "up") {
      return (
        <span
          style={{ ...archiveStyles.trendChip, ...archiveStyles.trendUp }}
          title="نسبت به رکورد قبلی افزایش داشته است"
        >
          <FaArrowUp /> صعودی
        </span>
      );
    }

    if (trendState === "down") {
      return (
        <span
          style={{ ...archiveStyles.trendChip, ...archiveStyles.trendDown }}
          title="نسبت به رکورد قبلی کاهش داشته است"
        >
          <FaArrowDown /> نزولی
        </span>
      );
    }

    if (trendState === "same") {
      return (
        <span
          style={{ ...archiveStyles.trendChip, ...archiveStyles.trendSame }}
          title="نسبت به رکورد قبلی تغییری نکرده است"
        >
          <FaMinus /> بدون تغییر
        </span>
      );
    }

    return <span style={archiveStyles.mutedText}>-</span>;
  };

  const renderStackedField = (label, content) => (
    <div style={archiveStyles.stackedRow}>
      <span style={archiveStyles.stackedLabel}>{label}</span>
      <span style={archiveStyles.stackedValue}>{content}</span>
    </div>
  );

  const renderMobileSortButton = () => (
    <button
      type="button"
      onClick={toggleSortOrder}
      style={archiveStyles.stackedTimeButton}
      title={sortOrder === "desc" ? "جدیدترین اول" : "قدیمی‌ترین اول"}
    >
      <span>زمان</span>
      {sortOrder === "desc" ? <FaArrowDown /> : <FaArrowUp />}
    </button>
  );

  const renderStackedContent = () => {
    if (totalRecords === 0 && !loading) {
      return (
        <div style={archiveStyles.empty}>رکوردی برای نمایش وجود ندارد.</div>
      );
    }

    if (loading && totalRecords === 0) {
      return <div style={archiveStyles.empty}>در حال دریافت آرشیو...</div>;
    }

    return (
      <div style={archiveStyles.stackedList}>
        {pageRecords.map((row, index) => {
          const globalIndex = pageStartIndex + index;
          const previousRow = getPreviousRecordForTrend(globalIndex);
          const setpoint = getRowSetpoint(row);
          const setpointUnit = row.setpoint_unit || row.unit || "";
          const limitState = isDigitalArchive
            ? "unknown"
            : getRowLimitState(row);
          const trendState = getRowTrendState(row, previousRow);

          const cardStyle = {
            ...archiveStyles.stackedCard,
            ...(limitState === "above" ? archiveStyles.stackedCardAbove : {}),
            ...(limitState === "below" ? archiveStyles.stackedCardBelow : {}),
          };

          return (
            <div key={`${row.time}-${globalIndex}`} style={cardStyle}>
              <div style={archiveStyles.stackedCardHeader}>
                <span style={archiveStyles.stackedIndex}>
                  ردیف {globalIndex + 1}
                </span>
                <span>{formatDateTime(row.time)}</span>
              </div>

              {isDigitalArchive ? (
                <>
                  {renderStackedField("value", renderDigitalCell(row.value))}
                  {renderStackedField(
                    "isConnect",
                    renderDigitalCell(row.isConnect),
                  )}
                  {renderStackedField("trig", renderDigitalCell(row.trig))}
                  {renderStackedField("enable", renderDigitalCell(row.enable))}
                </>
              ) : (
                <>
                  {renderStackedField(
                    "مقدار",
                    <span style={getValueBadgeStyle(limitState)}>
                      {formatValueWithUnit(row.value, row.unit)}
                    </span>,
                  )}
                  {renderStackedField(
                    "حد همان زمان",
                    setpoint !== null && setpoint !== undefined ? (
                      <span style={archiveStyles.setpointBadge}>
                        {formatValueWithUnit(setpoint, setpointUnit)}
                      </span>
                    ) : (
                      <span style={archiveStyles.mutedText}>-</span>
                    ),
                  )}
                  {renderStackedField("تغییر", renderTrend(trendState))}
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const overlayStyle = {
    ...archiveStyles.overlay,
    ...(isMobile
      ? {
          alignItems: "flex-start",
          padding: "8px",
          overflowY: "auto",
          overflowX: "hidden",
        }
      : {}),
  };

  const modalStyle = {
    ...archiveStyles.modal,
    ...(isMobile
      ? {
          width: "100%",
          maxHeight: "none",
          borderRadius: "14px",
        }
      : {}),
  };

  const toolbarStyle = {
    ...archiveStyles.toolbar,
    ...(isMobile
      ? {
          gap: "10px",
          padding: "10px 8px",
        }
      : {}),
  };

  const infoStyle = {
    ...archiveStyles.info,
    ...(isMobile
      ? {
          gap: "6px",
          padding: "8px",
          fontSize: "11px",
        }
      : {}),
  };

  const tableWrapperStyle = {
    ...archiveStyles.tableWrapper,
    ...(isMobile
      ? {
          padding: "10px 10px 8px",
          overflowX: "hidden",
          overflowY: "visible",
        }
      : {}),
  };

  const paginationStyle = {
    ...archiveStyles.pagination,
    ...(isMobile
      ? {
          gap: "6px",
          padding: "9px 8px 12px",
        }
      : {}),
  };

  return createPortal(
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={archiveStyles.header}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FaTable color={archiveTheme.icon} />
            <strong>آرشیو جدولی {displaySensorName}</strong>
          </div>

          <button onClick={onClose} style={archiveStyles.iconButton}>
            <FaTimes />
          </button>
        </div>

        <div style={toolbarStyle}>
          <div
            style={{
              ...archiveStyles.toolbarItem,
              justifyContent: "flex-start",
            }}
          >
            <div style={archiveStyles.filterItem}>
              <span>بازه:</span>
              <select
                value={range}
                onChange={(e) => setRange(e.target.value)}
                style={archiveStyles.select}
              >
                <option value="-1h">۱ ساعت اخیر</option>
                <option value="-6h">۶ ساعت اخیر</option>
                <option value="-12h">۱۲ ساعت اخیر</option>
                <option value="-24h">۲۴ ساعت اخیر</option>
                <option value="-7d">۷ روز اخیر</option>
                <option value="-30d">۳۰ روز اخیر</option>
                <option value="-90d">۹۰ روز اخیر</option>
              </select>
            </div>
          </div>

          <div
            style={{
              ...archiveStyles.toolbarItem,
              justifyContent: "center",
              gap: "14px",
              flexWrap: "wrap",
            }}
          >
            <div style={archiveStyles.filterItem}>
              <span>تعداد در صفحه:</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                style={archiveStyles.select}
              >
                <option value={20}>۲۰</option>
                <option value={50}>۵۰</option>
                <option value={100}>۱۰۰</option>
                <option value={200}>۲۰۰</option>
                <option value={500}>۵۰۰</option>
              </select>
            </div>
          </div>

          <div
            style={{ ...archiveStyles.toolbarItem, justifyContent: "flex-end" }}
          >
            <button
              onClick={loadArchive}
              style={archiveStyles.refreshButton}
              disabled={loading}
            >
              <FaSyncAlt />
              {loading ? "در حال دریافت..." : "بروزرسانی"}
            </button>
          </div>
        </div>

        <div style={infoStyle}>
          <span style={{ ...archiveStyles.infoItem, textAlign: "right" }}>
            دستگاه: {serial}
          </span>
          <span style={{ ...archiveStyles.infoItem, textAlign: "center" }}>
            سنسور: {displaySensorName}
          </span>
          <span style={{ ...archiveStyles.infoItem, textAlign: "left" }}>
            تعداد کل: {totalRecords} |{" "}
            {totalRecords === 0
              ? "نمایش: ۰"
              : `نمایش: ${pageStartIndex + 1}-${pageEndIndex}`}
          </span>
        </div>

        {error && <div style={archiveStyles.error}>{error}</div>}

        <div style={tableWrapperStyle}>
          {isMobile ? (
            <>
              <div style={{ marginBottom: "8px", textAlign: "left" }}>
                {renderMobileSortButton()}
              </div>
              {renderStackedContent()}
            </>
          ) : (
            <table style={archiveStyles.table}>
              <thead>
                {isDigitalArchive ? (
                  <tr>
                    <th style={archiveStyles.th}>ردیف</th>
                    <th style={archiveStyles.th}>{renderTimeHeader()}</th>
                    <th style={archiveStyles.th}>value</th>
                    <th style={archiveStyles.th}>isConnect</th>
                    <th style={archiveStyles.th}>trig</th>
                    <th style={archiveStyles.th}>enable</th>
                  </tr>
                ) : (
                  <tr>
                    <th style={archiveStyles.th}>ردیف</th>
                    <th style={archiveStyles.th}>{renderTimeHeader()}</th>
                    <th style={archiveStyles.th}>مقدار</th>
                    <th style={archiveStyles.th}>حد همان زمان</th>
                    <th style={archiveStyles.th}>تغییر</th>
                  </tr>
                )}
              </thead>

              <tbody>
                {totalRecords === 0 && !loading ? (
                  <tr>
                    <td
                      colSpan={isDigitalArchive ? 6 : 5}
                      style={archiveStyles.empty}
                    >
                      رکوردی برای نمایش وجود ندارد.
                    </td>
                  </tr>
                ) : loading && totalRecords === 0 ? (
                  <tr>
                    <td
                      colSpan={isDigitalArchive ? 6 : 5}
                      style={archiveStyles.empty}
                    >
                      در حال دریافت آرشیو...
                    </td>
                  </tr>
                ) : isDigitalArchive ? (
                  pageRecords.map((row, index) => {
                    const globalIndex = pageStartIndex + index;
                    const rowStyle = {
                      ...(globalIndex % 2 === 0
                        ? archiveStyles.rowEven
                        : archiveStyles.rowOdd),
                    };

                    return (
                      <tr key={`${row.time}-${globalIndex}`} style={rowStyle}>
                        <td style={archiveStyles.td}>{globalIndex + 1}</td>

                        <td style={archiveStyles.td}>
                          {formatDateTime(row.time)}
                        </td>

                        <td style={archiveStyles.td}>
                          {renderDigitalCell(row.value)}
                        </td>
                        <td style={archiveStyles.td}>
                          {renderDigitalCell(row.isConnect)}
                        </td>
                        <td style={archiveStyles.td}>
                          {renderDigitalCell(row.trig)}
                        </td>
                        <td style={archiveStyles.td}>
                          {renderDigitalCell(row.enable)}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  pageRecords.map((row, index) => {
                    const globalIndex = pageStartIndex + index;
                    const previousRow = getPreviousRecordForTrend(globalIndex);
                    const setpoint = getRowSetpoint(row);
                    const setpointUnit = row.setpoint_unit || row.unit || "";
                    const limitState = getRowLimitState(row);
                    const trendState = getRowTrendState(row, previousRow);

                    const rowStyle = {
                      ...(globalIndex % 2 === 0
                        ? archiveStyles.rowEven
                        : archiveStyles.rowOdd),
                      ...(limitState === "above" ? archiveStyles.rowAbove : {}),
                      ...(limitState === "below" ? archiveStyles.rowBelow : {}),
                      ...(limitState === "equal" ? archiveStyles.rowEqual : {}),
                    };

                    return (
                      <tr key={`${row.time}-${globalIndex}`} style={rowStyle}>
                        <td style={archiveStyles.td}>{globalIndex + 1}</td>

                        <td style={archiveStyles.td}>
                          {formatDateTime(row.time)}
                        </td>

                        <td style={archiveStyles.td}>
                          <span style={getValueBadgeStyle(limitState)}>
                            {formatValueWithUnit(row.value, row.unit)}
                          </span>
                        </td>

                        <td style={archiveStyles.td}>
                          {setpoint !== null && setpoint !== undefined ? (
                            <span style={archiveStyles.setpointBadge}>
                              {formatValueWithUnit(setpoint, setpointUnit)}
                            </span>
                          ) : (
                            <span style={archiveStyles.mutedText}>-</span>
                          )}
                        </td>

                        <td style={archiveStyles.td}>
                          {renderTrend(trendState)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>

        {totalRecords > 0 && (
          <div style={paginationStyle}>
            <button
              type="button"
              onClick={() => goToPage(1)}
              disabled={safeCurrentPage === 1}
              style={{
                ...archiveStyles.paginationButton,
                ...(safeCurrentPage === 1
                  ? archiveStyles.paginationButtonDisabled
                  : {}),
              }}
            >
              اول
            </button>

            <button
              type="button"
              onClick={() => goToPage(safeCurrentPage - 1)}
              disabled={safeCurrentPage === 1}
              style={{
                ...archiveStyles.paginationButton,
                ...(safeCurrentPage === 1
                  ? archiveStyles.paginationButtonDisabled
                  : {}),
              }}
            >
              قبلی
            </button>

            <span style={archiveStyles.pageInfo}>
              صفحه {safeCurrentPage} از {totalPages}
            </span>

            <button
              type="button"
              onClick={() => goToPage(safeCurrentPage + 1)}
              disabled={safeCurrentPage === totalPages}
              style={{
                ...archiveStyles.paginationButton,
                ...(safeCurrentPage === totalPages
                  ? archiveStyles.paginationButtonDisabled
                  : {}),
              }}
            >
              بعدی
            </button>

            <button
              type="button"
              onClick={() => goToPage(totalPages)}
              disabled={safeCurrentPage === totalPages}
              style={{
                ...archiveStyles.paginationButton,
                ...(safeCurrentPage === totalPages
                  ? archiveStyles.paginationButtonDisabled
                  : {}),
              }}
            >
              آخر
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default ArchiveTableModal;
